import Box from '@mui/material/Box';
import type { Ref, ReactNode } from 'react';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { brand } from '@/shared/config/brand';
import type { GuideCardData } from '../../types';
import { summarizeList } from '../../utils/guideCardData';
import { GuideCardPhoto } from './GuideCardPhoto';
import { GuideCardQrCode } from './GuideCardQrCode';
import { CARD_EXPORT_HEIGHT_PX, cardColors, cardFont, u } from './cardTokens';

const HEADER_HEIGHT = 92;
const FOOTER_HEIGHT = 52;

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

/** Shrinks long names so they stay within two lines beside the photo and QR. */
export function nameFontSize(name: string): number {
  const length = name.trim().length;
  if (length <= 16) return 36;
  if (length <= 24) return 31;
  return 27;
}

function CardField({
  icon,
  label,
  value,
  emphasis = false,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', gap: u(12), minWidth: 0 }}>
      <Box sx={{ color: cardColors.blue, display: 'flex', pt: u(2), '& svg': { fontSize: u(20) } }} aria-hidden>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          component="dt"
          sx={{
            fontSize: u(13),
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: cardColors.muted,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Box>
        <Box
          component="dd"
          title={value}
          sx={{
            m: 0,
            ...ellipsis,
            fontSize: emphasis ? u(22) : u(18),
            fontWeight: emphasis ? 700 : 500,
            letterSpacing: emphasis ? '0.03em' : 0,
            color: cardColors.text,
            lineHeight: 1.35,
          }}
        >
          {value}
        </Box>
      </Box>
    </Box>
  );
}

export interface GuideCardFrontProps {
  card: GuideCardData;
  /** 'screen' adds rounded corners and depth; 'export' is flat, full-bleed artwork. */
  surface?: 'screen' | 'export';
  /** Overrides `card.photo` (exports pass a pre-fetched data URL; null → placeholder). */
  photoSrc?: string | null;
  /** Overrides the logo URL (exports pass a pre-fetched data URL). */
  logoSrc?: string | null;
  cardRef?: Ref<HTMLDivElement>;
}

/**
 * The landscape identity card artwork (ID-1 proportions). Sized entirely in
 * container units: it fills the width of its parent and derives its height.
 */
export function GuideCardFront({ card, surface = 'screen', photoSrc, logoSrc, cardRef }: GuideCardFrontProps) {
  const isScreen = surface === 'screen';

  return (
    <Box sx={{ containerType: 'inline-size', width: '100%' }}>
      <Box
        ref={cardRef}
        component="article"
        aria-label={`Trplin guide identity card for ${card.name}`}
        data-testid="guide-card-front"
        sx={{
          position: 'relative',
          width: '100%',
          height: u(CARD_EXPORT_HEIGHT_PX),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
          bgcolor: cardColors.white,
          color: cardColors.text,
          fontFamily: cardFont,
          textAlign: 'left',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
          '& *': { boxSizing: 'border-box' },
          ...(isScreen && {
            borderRadius: u(22),
            boxShadow: '0 24px 60px rgba(16, 28, 58, 0.16), 0 2px 6px rgba(16, 28, 58, 0.06)',
          }),
        }}
      >
        <Box
          component="header"
          sx={{
            height: u(HEADER_HEIGHT),
            flexShrink: 0,
            px: u(32),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: cardColors.navy,
          }}
        >
          <Box
            component="img"
            src={logoSrc ?? brand.logoUrl}
            alt={brand.uiName}
            sx={{ height: u(50), width: 'auto', display: 'block' }}
          />
          <Box
            component="span"
            sx={{ color: cardColors.turquoise, fontSize: u(16), fontWeight: 700, letterSpacing: '0.16em' }}
          >
            GUIDE IDENTITY
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: u(26), p: u(28) }}>
          {/* Photo */}
          <Box sx={{ width: u(210), flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            <GuideCardPhoto src={photoSrc === undefined ? card.photo : photoSrc} name={card.name} />
          </Box>

          {/* Identity details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Box
              component="h2"
              data-testid="guide-card-name"
              title={card.name}
              sx={{
                m: 0,
                fontFamily: cardFont,
                fontSize: u(nameFontSize(card.name)),
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                color: cardColors.text,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {card.name}
            </Box>
            {card.location ? (
              <Box
                component="p"
                sx={{ m: 0, mt: u(6), fontSize: u(18), color: cardColors.muted, lineHeight: 1.4, ...ellipsis }}
              >
                {card.location}
              </Box>
            ) : null}
            <Box sx={{ borderTop: `${u(1.5)} solid ${cardColors.border}`, my: u(16), flexShrink: 0 }} />
            <Box component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: u(12), minWidth: 0 }}>
              <CardField icon={<BadgeOutlinedIcon />} label="Guide ID" value={card.guideId} emphasis />
              {card.languages.length > 0 ? (
                <CardField
                  icon={<TranslateOutlinedIcon />}
                  label="Languages"
                  value={summarizeList(card.languages)}
                />
              ) : null}
              {card.destinations.length > 0 ? (
                <CardField
                  icon={<PlaceOutlinedIcon />}
                  label="Destinations"
                  value={summarizeList(card.destinations)}
                />
              ) : null}
            </Box>
          </Box>

          {/* Verification QR */}
          <Box
            sx={{
              width: u(176),
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: u(10),
            }}
          >
            <Box
              sx={{
                p: u(6),
                bgcolor: cardColors.white,
                border: `${u(1.5)} solid ${cardColors.border}`,
                borderRadius: u(14),
              }}
            >
              <GuideCardQrCode value={card.profileUrl ?? card.identityUrl}size={u(156)} />
            </Box>
            <Box sx={{ fontSize: u(14), fontWeight: 700, letterSpacing: '0.1em', color: cardColors.text }}>
              SCAN TO VERIFY
            </Box>
          </Box>
        </Box>

        <Box
          component="footer"
          sx={{
            height: u(FOOTER_HEIGHT),
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: cardColors.blue,
            color: cardColors.white,
            fontSize: u(15),
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          Trusted local experiences · {brand.domain}
        </Box>
      </Box>
    </Box>
  );
}
